<template>
  <div class="w-full flex flex-col items-center">
    <!-- ===== HERO 紧凑轻量模式 (左提示文字与控制，右紧凑棋盘与选字) ===== -->
    <template v-if="isHero && currentLevel">
      <div class="w-full flex flex-col sm:flex-row items-center sm:items-start justify-center gap-3 sm:gap-4.5">
        <!-- 左侧面板：提示文字、关卡状态、释义、操作工具 -->
        <div class="flex flex-col justify-between w-full sm:w-52 shrink-0 text-left sm:self-stretch">
          <div>
            <!-- 关卡与难度状态条 -->
            <div class="flex items-center justify-between text-xs text-clay">
              <div class="flex items-center gap-1.5">
                <span class="inline-block size-1.5 rounded-full bg-seal" aria-hidden="true"></span>
                <span class="font-heading font-bold text-sm text-clay">第 {{ currentLevel.levelId }} 关</span>
                <span v-if="playerProgress?.rankTitle" class="rounded bg-amber-100 text-amber-900 border border-amber-300/60 px-1.5 py-0.5 text-[10px] font-medium font-serif">
                  【{{ playerProgress.rankTitle }}】
                </span>
                <span class="rounded bg-secondary/80 px-1.5 py-0.5 text-[11px] text-primary font-medium">
                  {{ difficultyLabel }}
                </span>
              </div>
              <!-- 移动端音效与大厅链接 -->
              <div class="flex items-center gap-2 sm:hidden">
                <button
                  type="button"
                  @click="toggleSound"
                  class="p-0.5 rounded text-muted hover:text-primary transition hover:bg-secondary/60 cursor-pointer text-sm leading-none select-none"
                  :title="soundMuted ? '开启音效' : '静音'"
                  :aria-label="soundMuted ? '开启音效' : '静音'"
                >
                  {{ soundMuted ? '🔇' : '🔊' }}
                </button>
                <a
                  href="/game/chengyu-tianci-jielong"
                  class="text-[11px] text-primary hover:underline font-semibold"
                  title="查看全部关卡目录"
                >
                  大厅 &rarr;
                </a>
              </div>
            </div>

            <div class="text-[11px] text-muted mt-0.5 flex items-center justify-between">
              <span>进度：已解 <strong class="text-primary font-bold">{{ solved.size }}</strong> / {{ currentLevel.idioms.length }} 词</span>
            </div>

            <!-- 核心提示文字区 (位于左侧，平时显示答题指引，达成时显示成语详情) -->
            <div class="my-2 rounded-lg border border-border/70 bg-secondary/30 p-2 text-left min-h-[62px] flex flex-col justify-center">
              <transition name="fade" mode="out-in">
                <!-- 若刚刚拼出成语，动态展示释义详情 -->
                <div v-if="toastIdiom" :key="toastIdiom.word" class="animate-pop">
                  <div class="flex items-center gap-1 font-bold text-xs text-emerald-800">
                    <span>🎉</span>
                    <span>{{ toastIdiom.word }}</span>
                    <span class="text-[10px] text-emerald-600 font-mono font-normal">
                      {{ idiomDetails[toastIdiom.word]?.pinyin || '' }}
                    </span>
                  </div>
                  <p class="text-[11px] text-emerald-700 leading-snug mt-1 line-clamp-2">
                    {{ idiomDetails[toastIdiom.word]?.explanation || '成语拼合成功！' }}
                  </p>
                </div>
                <!-- 平时状态：贴心答题提示 -->
                <div v-else key="tip">
                  <div class="text-xs font-semibold text-primary flex items-center gap-1">
                    <span>💡</span>
                    <span>答题提示</span>
                  </div>
                  <p class="text-[11px] text-muted leading-snug mt-0.5">
                    点选候选字补全成语，点击空格可切换焦点，错字点击可撤回。
                  </p>
                </div>
              </transition>
            </div>
          </div>

          <!-- 辅助操作工具按钮组 (位于左侧下方) -->
          <div class="flex items-center gap-1.5 mt-1 sm:mt-auto pt-1 flex-wrap sm:flex-nowrap">
            <button
              type="button"
              @click="hint"
              class="whitespace-nowrap shrink-0 rounded-md border border-primary bg-white px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary hover:text-white transition shadow-2xs cursor-pointer active:scale-95"
              title="获取一个正确的字作为提示"
            >
              💡 提示 ({{ hintCount }})
            </button>
            <button
              type="button"
              @click="reshuffle"
              class="whitespace-nowrap shrink-0 rounded-md border border-primary bg-white px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary hover:text-white transition shadow-2xs cursor-pointer active:scale-95"
              title="打乱候选字顺序"
            >
              🔀 重排
            </button>
            <button
              type="button"
              @click="leaderboardVisible = true"
              class="whitespace-nowrap shrink-0 rounded-md border border-amber-800/40 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100 transition shadow-2xs cursor-pointer font-serif flex items-center gap-0.5"
              title="查看群英金榜"
            >
              <span>📜</span>
              <span>金榜</span>
            </button>
            <button
              type="button"
              @click="toggleSound"
              class="hidden sm:inline-flex shrink-0 p-1 rounded text-muted hover:text-primary transition hover:bg-secondary/60 cursor-pointer text-xs leading-none select-none ml-0.5"
              :title="soundMuted ? '开启音效' : '静音'"
              :aria-label="soundMuted ? '开启音效' : '静音'"
            >
              {{ soundMuted ? '🔇' : '🔊' }}
            </button>
            <a
              href="/game/chengyu-tianci-jielong"
              class="hidden sm:inline-flex whitespace-nowrap text-[11px] text-primary hover:underline font-semibold ml-auto"
              title="查看全部关卡目录"
            >
              全部关卡 &rarr;
            </a>
          </div>
        </div>

        <!-- 右侧面板：紧凑棋盘与小巧候选字选择按钮 -->
        <div class="flex flex-col items-center shrink-0">
          <Board
            :level="currentLevel"
            :fills="fills"
            :locked="locked"
            :focus="focus"
            :shake-cells="shakeCells"
            :glow-cells="glowCells"
            :answer="answerMap"
            :blank-set="blankSet"
            :is-hero="true"
            @cell-click="clickCell"
          />

          <!-- 底部候选字盘 -->
          <Keypad
            :cands="cands"
            :is-hero="true"
            @select-cand="pickCand"
          />
        </div>
      </div>
    </template>

    <!-- ===== 独立全页模式 (保持经典大棋盘布局) ===== -->
    <template v-else-if="!isHero">
      <!-- 关卡状态栏 (独立全页模式) -->
      <div class="mb-4 flex items-center justify-between w-full max-w-md px-2 text-sm text-muted">
        <div class="flex items-center gap-2">
          <span class="font-bold text-clay text-base">第 {{ currentLevel?.levelId || 1 }} 关</span>
          <span v-if="playerProgress?.rankTitle" class="rounded bg-amber-100 text-amber-900 border border-amber-300/60 px-2 py-0.5 text-xs font-medium font-serif">
            【{{ playerProgress.rankTitle }}】
          </span>
          <span class="rounded bg-secondary px-2 py-0.5 text-xs text-primary font-medium">
            {{ difficultyLabel }}
          </span>
        </div>
        <div class="flex items-center gap-3">
          <!-- 音效切换按钮 -->
          <button
            type="button"
            @click="toggleSound"
            class="p-1 rounded-md text-muted hover:text-primary transition hover:bg-secondary/60 cursor-pointer text-base leading-none select-none"
            :title="soundMuted ? '开启音效' : '静音'"
            :aria-label="soundMuted ? '开启音效' : '静音'"
          >
            {{ soundMuted ? '🔇' : '🔊' }}
          </button>
          <button type="button" class="text-xs text-primary hover:underline cursor-pointer" @click="shareLevel">分享</button>
          <div>
            <span>已解成语：</span>
            <span class="font-bold text-primary">{{ solved.size }}</span>
            <span> / {{ currentLevel?.idioms.length || 0 }}</span>
          </div>
        </div>
      </div>

      <!-- 棋盘网格或加载中状态 -->
      <div v-if="isLoadingLevel && !currentLevel" class="aspect-square max-w-sm w-full mx-auto rounded-2xl bg-secondary/30 border border-border flex flex-col items-center justify-center p-6 text-center">
        <div class="size-8 rounded-full border-2 border-primary border-t-transparent animate-spin mb-3"></div>
        <p class="text-sm font-medium text-clay">正在载入关卡题盘…</p>
      </div>

      <template v-else-if="currentLevel">
        <div class="relative w-full flex flex-col items-center">
          <Board
            :level="currentLevel"
            :fills="fills"
            :locked="locked"
            :focus="focus"
            :shake-cells="shakeCells"
            :glow-cells="glowCells"
            :answer="answerMap"
            :blank-set="blankSet"
            :is-hero="false"
            @cell-click="clickCell"
          />

          <!-- 独立页成语完成 Toast 提示浮窗 -->
          <div class="mt-2.5 h-13 w-full max-w-md flex items-center justify-center">
            <transition name="fade">
              <div
                v-if="toastIdiom"
                class="animate-pop rounded-xl border border-emerald-300 bg-emerald-50 px-3.5 py-1.5 text-center shadow-xs w-full"
              >
                <div class="flex items-center justify-center gap-2">
                  <span class="font-bold text-emerald-800 text-sm">{{ toastIdiom.word }}</span>
                  <span class="text-xs text-emerald-600 font-mono">
                    {{ idiomDetails[toastIdiom.word]?.pinyin || '' }}
                  </span>
                </div>
                <p class="text-xs text-emerald-700 truncate mt-0.5">
                  {{ idiomDetails[toastIdiom.word]?.explanation || '成语拼合成功！' }}
                </p>
              </div>
            </transition>
          </div>
        </div>

        <!-- 底部候选字盘 -->
        <Keypad
          :cands="cands"
          :is-hero="false"
          @select-cand="pickCand"
        />

        <!-- 辅助工具栏 -->
        <div class="mt-4 sm:mt-5 flex flex-wrap justify-center gap-2.5">
          <button
            type="button"
            @click="hint"
            class="rounded-full border border-primary bg-white px-3.5 py-1.5 text-xs sm:text-sm font-medium text-primary hover:bg-primary hover:text-white transition-all shadow-2xs cursor-pointer active:scale-95"
          >
            💡 求助提示 ({{ hintCount }})
          </button>
          <button
            type="button"
            @click="reshuffle"
            class="rounded-full border border-primary bg-white px-3.5 py-1.5 text-xs sm:text-sm font-medium text-primary hover:bg-primary hover:text-white transition-all shadow-2xs cursor-pointer active:scale-95"
          >
            🔀 乱序重排
          </button>
          <button
            type="button"
            @click="leaderboardVisible = true"
            class="rounded-full border border-amber-800/40 bg-amber-50 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-amber-900 hover:bg-amber-100 transition-all shadow-2xs cursor-pointer font-serif flex items-center gap-1"
          >
            <span>📜</span>
            <span>群英金榜</span>
          </button>
          <button
            type="button"
            @click="dictOpen = true"
            class="rounded-full border border-primary bg-white px-4 py-2 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-all shadow-xs cursor-pointer"
          >
            📖 本关词表
          </button>
        </div>
      </template>
    </template>

    <!-- 加载中全局兜底 (Hero 模式无数据时) -->
    <div v-if="isHero && isLoadingLevel && !currentLevel" class="aspect-video max-w-sm w-full mx-auto rounded-xl bg-secondary/30 border border-border flex flex-col items-center justify-center p-5 text-center">
      <div class="size-7 rounded-full border-2 border-primary border-t-transparent animate-spin mb-2"></div>
      <p class="text-xs font-medium text-clay">正在载入成语题盘…</p>
    </div>

    <!-- 本关词表预览弹窗 (仅全页模式可用) -->
    <div
      v-if="dictOpen && currentLevel"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs"
      @click="dictOpen = false"
    >
      <div
        class="animate-pop max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl border-2 border-primary bg-white p-6 shadow-2xl"
        @click.stop
      >
        <div class="mb-4 flex items-center justify-between border-b border-border pb-2">
          <h2 class="font-heading text-xl font-bold text-primary">本关成语列表</h2>
          <button type="button" @click="dictOpen = false" class="text-2xl text-muted cursor-pointer">×</button>
        </div>
        <ul class="space-y-3">
          <li
            v-for="(item, idx) in currentLevel.idioms"
            :key="item.id"
            class="border-b border-border/60 pb-2.5 last:border-none"
          >
            <div class="flex items-center gap-2">
              <span class="rounded bg-secondary px-1.5 py-0.5 text-xs text-primary font-mono">
                {{ item.direction === 'horizontal' ? '横' : '竖' }}{{ idx + 1 }}
              </span>
              <span v-if="solved.has(item.id)" class="font-bold text-emerald-700">
                {{ item.word }}
              </span>
              <span v-else class="text-muted font-medium">（答题中未解开）</span>
            </div>
            <p v-if="solved.has(item.id)" class="text-xs text-clay mt-1 leading-relaxed">
              {{ idiomDetails[item.word]?.explanation || '已解锁该词' }}
            </p>
          </li>
        </ul>
      </div>
    </div>

    <!-- 通关弹窗 -->
    <WinModal
      v-if="currentLevel"
      :show="won"
      :level="currentLevel"
      :is-last="currentLevel.levelId >= totalLevels"
      :details="idiomDetails"
      :is-hero="isHero"
      :checkpoint-result="checkpointResult"
      @close="won = false"
      @next="nextLevel"
      @share="shareLevel"
      @restart="restartFromFirstLevel"
      @open-leaderboard="leaderboardVisible = true"
    />

    <!-- 通用金榜排行榜抽屉 -->
    <GameLeaderboardDrawer
      :visible="leaderboardVisible"
      game-code="chengyu_crossword"
      @close="leaderboardVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { LevelItem, LevelIdiom, Fill, Cand, IdiomDetail } from './lib/types'
import { sfx, setMuted } from './lib/audio'
import Board from './components/Board.vue'
import Keypad from './components/Keypad.vue'
import WinModal from './components/Modals/WinModal.vue'
import GameLeaderboardDrawer from '@/components/game/GameLeaderboardDrawer.vue'
import {
  getPlayerProgress,
  checkpoint,
  consumeProp,
  mergeGuest,
  PlayerProgressData,
  CheckpointResultData
} from '@/api/game'
import { isAuthenticated } from '@/utils/auth'
import { showSuccess, showWarning } from '@/utils/toast'
import { trackLevelStart, trackLevelUp, trackGameDwell } from '@/analytics/track'
import { shareChengyu } from '@/vue-apps/share/games'

const props = withDefaults(
  defineProps<{
    initialLevel: LevelItem | null
    totalLevels: number
    isHero?: boolean
  }>(),
  {
    initialLevel: null,
    totalLevels: 100,
    isHero: false,
  }
)

const currentLevel = ref<LevelItem | null>(props.initialLevel)
const isLoadingLevel = ref(false)
const soundMuted = ref(false)

const shareLevel = () => {
  if (currentLevel.value) {
    shareChengyu(currentLevel.value.levelId)
  }
}

const fills = ref<Record<string, Fill>>({})
const locked = ref<Set<string>>(new Set())
const solved = ref<Set<string>>(new Set())
const cands = ref<Cand[]>([])
const focus = ref<string | null>(null)
const shakeCells = ref<Set<string>>(new Set())
const glowCells = ref<Set<string>>(new Set())
const won = ref(false)
const dictOpen = ref(false)
const toastIdiom = ref<LevelIdiom | null>(null)
const idiomDetails = ref<Record<string, IdiomDetail>>({})

// 游戏进度、仕途段位与排行榜资产
const playerProgress = ref<PlayerProgressData | null>(null)
const checkpointResult = ref<CheckpointResultData | null>(null)
const leaderboardVisible = ref(false)
const levelStartTime = ref(Date.now())
const levelStepsCount = ref(0)
const hintCount = computed(() => playerProgress.value?.props?.hint ?? 3)

// 停留与参与度统计
let sessionStartTime = Date.now()
const interactionCount = ref(0)
const levelsCompletedInSession = ref(0)
const hasReportedExitDwell = ref(false)
const heartbeatTimers: number[] = []

const k = (r: number, c: number) => `${r},${c}`

// 计算属性：当前关卡答案映射
const answerMap = computed(() => {
  const m = new Map<string, string>()
  if (!currentLevel.value) return m
  for (const item of currentLevel.value.idioms) {
    for (let i = 0; i < item.word.length; i++) {
      const r = item.direction === 'vertical' ? item.startRow + i : item.startRow
      const c = item.direction === 'vertical' ? item.startCol : item.startCol + i
      m.set(k(r, c), item.word[i])
    }
  }
  return m
})

// 计算属性：空格集合
const blankSet = computed(() => {
  if (!currentLevel.value) return new Set<string>()
  return new Set(currentLevel.value.initialBlanks.map((b) => k(b.row, b.col)))
})

// 难度标签
const difficultyLabel = computed(() => {
  const id = currentLevel.value?.levelId || 1
  if (id <= 15) return '新手村'
  if (id <= 50) return '秀才级'
  if (id <= 150) return '举人级'
  return '进士级'
})

// 音效开关切换
function toggleSound() {
  soundMuted.value = !soundMuted.value
  setMuted(soundMuted.value)
  try {
    localStorage.setItem('chengyu_sound_muted', String(soundMuted.value))
  } catch {}
}

// 寻找包含特定格子的成语列表
function idiomsAt(key: string): LevelIdiom[] {
  if (!currentLevel.value) return []
  return currentLevel.value.idioms.filter((item) => {
    for (let i = 0; i < item.word.length; i++) {
      const r = item.direction === 'vertical' ? item.startRow + i : item.startRow
      const c = item.direction === 'vertical' ? item.startCol : item.startCol + i
      if (k(r, c) === key) return true
    }
    return false
  })
}

function getCharInBoard(key: string, currentFills: Record<string, Fill>): string | undefined {
  if (blankSet.value.has(key)) {
    return currentFills[key]?.ch
  }
  return answerMap.value.get(key)
}

// 寻找下一个焦点
function getNextFocus(fromKey: string, currentFills: Record<string, Fill>, currentLocked: Set<string>): string | null {
  if (!currentLevel.value) return null
  const isEmpty = (key: string) => blankSet.value.has(key) && !currentFills[key] && !currentLocked.has(key)

  // 优先同成语中的下一个空格
  for (const idiom of idiomsAt(fromKey)) {
    for (let i = 0; i < idiom.word.length; i++) {
      const r = idiom.direction === 'vertical' ? idiom.startRow + i : idiom.startRow
      const c = idiom.direction === 'vertical' ? idiom.startCol : idiom.startCol + i
      const cell = k(r, c)
      if (isEmpty(cell)) return cell
    }
  }

  // 兜底找盘面第一个空格
  for (const b of currentLevel.value.initialBlanks) {
    const cell = k(b.row, b.col)
    if (isEmpty(cell)) return cell
  }
  return null
}

// 初始化/切换关卡数据
function initLevelState(levelData: LevelItem) {
  currentLevel.value = levelData
  fills.value = {}
  locked.value = new Set()
  solved.value = new Set()
  shakeCells.value = new Set()
  glowCells.value = new Set()
  won.value = false
  toastIdiom.value = null
  levelStartTime.value = Date.now()
  levelStepsCount.value = 0
  checkpointResult.value = null

  cands.value = levelData.candidateChars.map((ch) => ({ ch, used: false }))
  focus.value = levelData.initialBlanks[0] ? k(levelData.initialBlanks[0].row, levelData.initialBlanks[0].col) : null
  trackLevelStart({
    level_name: 'chengyu',
    level: levelData.levelId,
    location: props.isHero ? 'home_hero' : 'standalone',
  })

  // 异步预加载本关涉及的成语词典释义
  loadIdiomDetails(levelData.idioms)
}

// 独立关卡游玩页若有 SSR 注入的 initialLevel，在 setup 阶段立即初始化题盘状态
if (props.initialLevel && !props.isHero) {
  initLevelState(props.initialLevel)
}

// 异步拉取指定关卡数据 (优先使用 JSON API)
async function fetchAndLoadLevel(levelId: number): Promise<boolean> {
  isLoadingLevel.value = true
  try {
    const res = await fetch(`/game/chengyu-tianci-jielong/level/${levelId}?format=json`, {
      headers: { Accept: 'application/json' },
    })
    if (res.ok) {
      const contentType = res.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        const data = await res.json() as LevelItem
        initLevelState(data)
        isLoadingLevel.value = false
        return true
      }
    }

    // 容错降级：抓取 HTML 解析
    const htmlRes = await fetch(`/game/chengyu-tianci-jielong/level/${levelId}`)
    if (htmlRes.ok) {
      const html = await htmlRes.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const mountEl = doc.getElementById('chengyu-game-island') || doc.getElementById('hero-chengyu-game-island')
      const rawData = mountEl?.getAttribute('data-level-data')
      if (rawData) {
        const data = JSON.parse(rawData) as LevelItem
        initLevelState(data)
        isLoadingLevel.value = false
        return true
      }
    }
  } catch (e) {
    console.error('[chengyu-game] 关卡加载失败:', e)
  }
  isLoadingLevel.value = false
  return false
}

// 点击格子
function clickCell(key: string) {
  interactionCount.value++
  if (!blankSet.value.has(key) || locked.value.has(key)) return

  // 点击已填自选字：撤回至候选字盘
  if (fills.value[key]) {
    sfx.recall()
    const fillItem = fills.value[key]
    if (fillItem && cands.value[fillItem.cand]) {
      cands.value[fillItem.cand].used = false
    }
    const newFills = { ...fills.value }
    delete newFills[key]
    fills.value = newFills
  }
  focus.value = key
}

// 点选候选字
function pickCand(idx: number) {
  interactionCount.value++
  levelStepsCount.value++
  if (!focus.value || cands.value[idx]?.used || locked.value.has(focus.value)) return
  sfx.click()

  const currentFocus = focus.value
  const newFills = { ...fills.value }

  // 若当前聚焦格已有字，先将其放回候选池
  if (newFills[currentFocus]) {
    const prevCandIdx = newFills[currentFocus].cand
    if (cands.value[prevCandIdx]) {
      cands.value[prevCandIdx].used = false
    }
  }

  cands.value[idx].used = true
  newFills[currentFocus] = { ch: cands.value[idx].ch, cand: idx }
  fills.value = newFills

  evaluate(currentFocus, newFills)
}

// 评估成语闭环
function evaluate(lastFillKey: string, currentFills: Record<string, Fill>) {
  if (!currentLevel.value) return
  const newLocked = new Set(locked.value)
  const newSolved = new Set(solved.value)
  const badCells = new Set<string>()
  const goodCells = new Set<string>()
  let lastSolvedIdiom: LevelIdiom | null = null

  for (const idiom of idiomsAt(lastFillKey)) {
    if (newSolved.has(idiom.id)) continue

    const cells: string[] = []
    const chars: (string | undefined)[] = []
    for (let i = 0; i < idiom.word.length; i++) {
      const r = idiom.direction === 'vertical' ? idiom.startRow + i : idiom.startRow
      const c = idiom.direction === 'vertical' ? idiom.startCol : idiom.startCol + i
      const cell = k(r, c)
      cells.push(cell)
      chars.push(getCharInBoard(cell, currentFills))
    }

    // 尚未填满
    if (chars.some((c) => !c)) continue

    if (chars.join('') === idiom.word) {
      newSolved.add(idiom.id)
      cells.forEach((c) => {
        newLocked.add(c)
        goodCells.add(c)
      })
      lastSolvedIdiom = idiom
    } else {
      // 填错，需回弹
      cells.forEach((c) => {
        if (blankSet.value.has(c) && currentFills[c] && !newLocked.has(c)) {
          const candIdx = currentFills[c].cand
          if (cands.value[candIdx]) {
            cands.value[candIdx].used = false
          }
          delete currentFills[c]
          badCells.add(c)
        }
      })
    }
  }

  fills.value = currentFills
  locked.value = newLocked
  solved.value = newSolved

  if (badCells.size > 0) {
    sfx.error()
    shakeCells.value = badCells
    setTimeout(() => {
      shakeCells.value = new Set()
    }, 450)
    focus.value = Array.from(badCells)[0] || null
  } else {
    focus.value = getNextFocus(lastFillKey, currentFills, newLocked)
  }

  if (goodCells.size > 0) {
    glowCells.value = goodCells
    setTimeout(() => {
      glowCells.value = new Set()
    }, 1700)

    if (newSolved.size === currentLevel.value.idioms.length) {
      // 全关通关
      sfx.win()
      handleWin()
    } else {
      sfx.success()
      if (lastSolvedIdiom) {
        toastIdiom.value = lastSolvedIdiom
        setTimeout(() => {
          if (toastIdiom.value === lastSolvedIdiom) {
            toastIdiom.value = null
          }
        }, 2600)
      }
    }
  }
}

// 通关处理
function handleWin() {
  if (!currentLevel.value) return
  levelsCompletedInSession.value++
  const currentId = currentLevel.value.levelId
  trackLevelUp(currentId, undefined, props.isHero ? 'home_hero' : 'standalone')
  const nextId = currentId + 1
  try {
    const saved = localStorage.getItem('chengyu_guest_highest_level')
    const currentHighest = saved ? parseInt(saved, 10) : 1
    if (nextId > currentHighest) {
      localStorage.setItem('chengyu_guest_highest_level', String(nextId))
    }
  } catch {}

  // 权威通关结算上报 (防作弊验证与仕途晋升)
  const elapsedMs = Math.max(1600, Date.now() - levelStartTime.value)
  const stepsCount = Math.max(4, levelStepsCount.value)
  const attemptId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : 'att-' + Date.now()

  checkpoint('chengyu_crossword', {
    attemptId,
    levelId: currentId,
    elapsedMs,
    stepsCount,
    manifestVersion: '20261002_v1'
  }).then((res) => {
    if (res.data) {
      checkpointResult.value = res.data
      if (playerProgress.value) {
        playerProgress.value.highestLevel = res.data.highestLevel
        playerProgress.value.rankTitle = res.data.rankTitle
        playerProgress.value.rankLevel = res.data.rankLevel
        playerProgress.value.totalStars = res.data.totalStars
      }
    }
  }).catch((e) => {
    console.warn('关卡结算上报异常:', e)
  })

  setTimeout(() => {
    won.value = true
  }, 500)
}

// 求助提示
async function hint() {
  interactionCount.value++
  if (!currentLevel.value) return

  // 道具库存权威核销 (防客户端作弊刷道具)
  if (hintCount.value <= 0) {
    showWarning('提示锦囊已用尽，继续闯关即可获赠更多锦囊！')
    return
  }

  const consumeId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : 'c-' + Date.now()

  try {
    const resp = await consumeProp('chengyu_crossword', {
      consumeId,
      propType: 'hint',
      count: 1
    })
    if (playerProgress.value && resp.data) {
      if (!playerProgress.value.props) playerProgress.value.props = {}
      playerProgress.value.props.hint = resp.data.remainingCount
    }
  } catch (e: any) {
    console.warn('提示道具核销异常，本地先行扣减:', e)
    if (playerProgress.value?.props?.hint) {
      playerProgress.value.props.hint--
    }
  }

  const blanks = currentLevel.value.initialBlanks.map((b) => k(b.row, b.col))
  const openBlanks = blanks.filter((b) => !locked.value.has(b) && fills.value[b]?.ch !== answerMap.value.get(b))
  if (openBlanks.length === 0) return

  const target = openBlanks[Math.floor(Math.random() * openBlanks.length)]
  const neededChar = answerMap.value.get(target)
  if (!neededChar) return

  const newFills = { ...fills.value }

  // 如果目标格已有错误字，先撤回
  if (newFills[target]) {
    const oldIdx = newFills[target].cand
    if (cands.value[oldIdx]) {
      cands.value[oldIdx].used = false
    }
    delete newFills[target]
  }

  // 从候选池找目标字
  let candIdx = cands.value.findIndex((c) => !c.used && c.ch === neededChar)
  if (candIdx < 0) {
    const holder = Object.keys(newFills).find((x) => newFills[x]?.ch === neededChar && !locked.value.has(x))
    if (holder) {
      candIdx = newFills[holder].cand
      if (cands.value[candIdx]) {
        cands.value[candIdx].used = false
      }
      delete newFills[holder]
    }
  }

  if (candIdx >= 0 && cands.value[candIdx]) {
    cands.value[candIdx].used = true
    newFills[target] = { ch: neededChar, cand: candIdx }
    sfx.click()
    evaluate(target, newFills)
  }
}

// 乱序重排
function reshuffle() {
  interactionCount.value++
  const order = cands.value.map((_, i) => i)
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = order[i]
    order[i] = order[j]
    order[j] = temp
  }
  const newCands = order.map((i) => cands.value[i])
  const oldToNew = new Map(order.map((oldI, newI) => [oldI, newI]))
  const newFills: Record<string, Fill> = {}
  for (const [key, v] of Object.entries(fills.value)) {
    newFills[key] = { ch: v.ch, cand: oldToNew.get(v.cand) ?? v.cand }
  }
  cands.value = newCands
  fills.value = newFills
}

// 异步加载成语释义
async function loadIdiomDetails(idioms: LevelIdiom[]) {
  for (const item of idioms) {
    if (idiomDetails.value[item.word]) continue
    try {
      const res = await fetch(`/game/chengyu-tianci-jielong/idiom/${encodeURIComponent(item.word)}?format=json`, {
        headers: { Accept: 'application/json' },
      })
      if (res.ok) {
        const data = await res.json()
        idiomDetails.value[item.word] = data
      }
    } catch {
      // 容错降级
    }
  }
}

// 同步更新页面外部 SSR 静态元素（面包屑与大标题、上下关超链接）
function updatePageStaticElements(levelId: number, totalLevels: number) {
  if (typeof document === 'undefined') return

  // 更新浏览器标题
  document.title = `第 ${levelId} 关 - 成语填字接龙 - 谜语猜`

  // 更新 Canonical 标签
  const canonicalLink = document.querySelector('link[rel="canonical"]')
  if (canonicalLink) {
    canonicalLink.setAttribute('href', `${window.location.origin}/game/chengyu-tianci-jielong/level/${levelId}`)
  }

  // 更新面包屑最后一级
  const breadcrumbCurrent = document.querySelector('nav[aria-label="Breadcrumb"] span.text-clay')
  if (breadcrumbCurrent) {
    breadcrumbCurrent.textContent = `第 ${levelId} 关`
  }

  // 更新顶部关卡主标题
  const headingH1 = document.querySelector('header h1')
  if (headingH1) {
    headingH1.textContent = `第 ${levelId} 关`
  }

  // 同步更新上一关 / 下一关超链接
  const prevLevel = levelId > 1 ? levelId - 1 : 0
  const nextLevel = levelId < totalLevels ? levelId + 1 : 0
  const headerLinks = document.querySelectorAll('header a[href*="/game/chengyu-tianci-jielong/level/"]')
  headerLinks.forEach((el) => {
    const text = el.textContent?.trim() || ''
    if (text.includes('上一关')) {
      if (prevLevel > 0) {
        el.setAttribute('href', `/game/chengyu-tianci-jielong/level/${prevLevel}`)
        ;(el as HTMLElement).style.display = ''
      } else {
        ;(el as HTMLElement).style.display = 'none'
      }
    } else if (text.includes('下一关')) {
      if (nextLevel > 0) {
        el.setAttribute('href', `/game/chengyu-tianci-jielong/level/${nextLevel}`)
        ;(el as HTMLElement).style.display = ''
      } else {
        ;(el as HTMLElement).style.display = 'none'
      }
    }
  })
}

// 进入下一关
async function nextLevel() {
  if (!currentLevel.value) return
  const nextId = currentLevel.value.levelId + 1
  if (nextId > props.totalLevels) return

  if (props.isHero) {
    // Hero 模式：保持主页 URL 纯净不变，卡片内直接平滑载入下一关
    won.value = false
    await fetchAndLoadLevel(nextId)
    return
  }

  // 独立游戏页模式：全屏导航与更新历史记录
  won.value = false
  window.history.pushState({ levelId: nextId }, '', `/game/chengyu-tianci-jielong/level/${nextId}`)

  const ok = await fetchAndLoadLevel(nextId)
  if (!ok) {
    window.location.href = `/game/chengyu-tianci-jielong/level/${nextId}`
  } else {
    updatePageStaticElements(nextId, props.totalLevels)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// 重新从第 1 关开始挑战
async function restartFromFirstLevel() {
  won.value = false
  try {
    localStorage.setItem('chengyu_guest_highest_level', '1')
  } catch {}

  if (props.isHero) {
    await fetchAndLoadLevel(1)
    return
  }

  window.location.href = '/game/chengyu-tianci-jielong/level/1'
}

function handlePopState(event: PopStateEvent) {
  if (event.state && event.state.levelId) {
    const targetId = event.state.levelId
    fetchAndLoadLevel(targetId).then((ok) => {
      if (ok) {
        updatePageStaticElements(targetId, props.totalLevels)
      } else {
        window.location.reload()
      }
    })
  } else {
    window.location.reload()
  }
}

// 上报游戏停留情况
function reportDwellTime() {
  if (hasReportedExitDwell.value) return
  const elapsedSec = Math.round((Date.now() - sessionStartTime) / 1000)
  if (elapsedSec >= 2 && (interactionCount.value > 0 || levelsCompletedInSession.value > 0 || elapsedSec >= 10)) {
    hasReportedExitDwell.value = true
    trackGameDwell({
      game_name: 'chengyu',
      location: props.isHero ? 'home_hero' : 'standalone',
      duration_seconds: elapsedSec,
      levels_completed: levelsCompletedInSession.value,
      interactions: interactionCount.value,
      current_level: currentLevel.value?.levelId || 1,
    })
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    reportDwellTime()
  }
}

onMounted(async () => {
  sessionStartTime = Date.now()

  // 读取静音设置
  try {
    soundMuted.value = localStorage.getItem('chengyu_sound_muted') === 'true'
    setMuted(soundMuted.value)
  } catch {}

  // 1. 同步远端玩家进度与仕途段位
  try {
    const progRes = await getPlayerProgress('chengyu_crossword')
    if (progRes?.data) {
      playerProgress.value = progRes.data
    }
  } catch (e) {
    console.warn('获取玩家远端进度失败，降级本地存储:', e)
  }

  // 2. 若当前为登录态，自动将游客进度资产合并
  if (isAuthenticated()) {
    try {
      const mergeRes = await mergeGuest('chengyu_crossword')
      if (mergeRes?.data?.merged) {
        showSuccess(`已成功同步游客进度至账号（最高关：第 ${mergeRes.data.highestLevel} 关 · ${mergeRes.data.rankTitle}）`)
        const refreshProg = await getPlayerProgress('chengyu_crossword')
        if (refreshProg?.data) {
          playerProgress.value = refreshProg.data
        }
      }
    } catch (e) {
      console.warn('游客进度合并处理跳过或已完成:', e)
    }
  }

  // 关卡初始化与断点续关
  if (props.isHero) {
    // Hero 首页模式：支持断点续关，优先读取远端最高关卡，次选 localStorage
    let targetLevelId = props.initialLevel ? props.initialLevel.levelId : 1
    if (playerProgress.value?.highestLevel && playerProgress.value.highestLevel > 0) {
      targetLevelId = Math.min(props.totalLevels, playerProgress.value.highestLevel)
    } else {
      try {
        const saved = localStorage.getItem('chengyu_guest_highest_level')
        if (saved) {
          const parsed = parseInt(saved, 10)
          if (parsed >= 1 && parsed <= props.totalLevels) {
            targetLevelId = parsed
          }
        }
      } catch {}
    }

    if (currentLevel.value && currentLevel.value.levelId === targetLevelId) {
      if (cands.value.length === 0) {
        initLevelState(currentLevel.value)
      }
    } else {
      await fetchAndLoadLevel(targetLevelId)
    }
  } else {
    // 独立关卡游玩页模式：严格以当前页面请求的关卡为准，绝不可被历史最高进度覆盖
    if (props.initialLevel) {
      if (!currentLevel.value || currentLevel.value.levelId !== props.initialLevel.levelId) {
        initLevelState(props.initialLevel)
      }
    } else {
      // 容错降级：若 SSR 未注入 initialLevel，优先从当前 URL 路径解析关卡编号
      const match = typeof window !== 'undefined' ? window.location.pathname.match(/\/level\/(\d+)/) : null
      const urlLevelId = match ? parseInt(match[1], 10) : 1
      await fetchAndLoadLevel(urlLevelId > 0 && urlLevelId <= props.totalLevels ? urlLevelId : 1)
    }
    window.addEventListener('popstate', handlePopState)
  }

  // 监听停留情况事件
  window.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('beforeunload', reportDwellTime)

  // 阶梯停留时长里程碑统计 (30s, 60s, 180s)
  const milestones = [30, 60, 180]
  for (const sec of milestones) {
    const timer = window.setTimeout(() => {
      if (document.visibilityState === 'visible' && (interactionCount.value > 0 || levelsCompletedInSession.value > 0)) {
        trackGameDwell({
          game_name: 'chengyu',
          location: props.isHero ? 'home_hero' : 'standalone',
          duration_seconds: sec,
          levels_completed: levelsCompletedInSession.value,
          interactions: interactionCount.value,
          current_level: currentLevel.value?.levelId || 1,
        })
      }
    }, sec * 1000)
    heartbeatTimers.push(timer)
  }
})

onUnmounted(() => {
  reportDwellTime()
  for (const t of heartbeatTimers) {
    clearTimeout(t)
  }
  if (!props.isHero) {
    window.removeEventListener('popstate', handlePopState)
  }
  window.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('beforeunload', reportDwellTime)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

